import * as React from 'react';
import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { Typography } from '@canvas-logic/clp-shared-ui';

import { Backdrop } from '@/ui-kit/components/Backdrop/Backdrop';
import { ModalCloseButton } from '@/ui-kit/components/ModalCloseButton/ModalCloseButton';

import { useSwipable } from './hooks/useSwipable';
import { useResizeObserver } from '../../hooks';

import styles from './BottomSheet.module.css';

interface IProps {
  variant?: 'default' | 'main';
  title?: string | JSX.Element;
  onClose: () => void;
  onClick?: () => void;
  onContentResize?: (height: number) => void;
  noClose?: boolean;
  renderActions?: () => JSX.Element;
  backdrop?: boolean;
  level?: number;
  // @todo: rename
  maxContentHeight?: number;
}

export interface BottomSheetContentProps {
  height?: 'content' | '*';
  className?: string;
  title?: string | ReactNode;
}

export type IBottomSheet = React.FC<React.PropsWithChildren<IProps>> & {
  Container: React.FC<React.PropsWithChildren>;
  Content: React.FC<React.PropsWithChildren<BottomSheetContentProps>>;
  Footer: React.FC<React.PropsWithChildren>;
};

const BottomSheet: IBottomSheet = ({
  variant = 'default',
  title,
  onClose,
  onClick = () => {},
  onContentResize,
  noClose,
  renderActions,
  backdrop = true,
  children,
  level,
  maxContentHeight,
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const { sheetRef, contentRef, handleRef, sheetStyle, isCollapsed, progress } = useSwipable({
    onCollapse: onClose,
    swipeDownBoundaryCoefficient: 0.7,
  });

  const dimensions = useResizeObserver(container);

  useEffect(() => {
    if (onContentResize && dimensions.height) {
      onContentResize(dimensions.height);
    }
  }, [dimensions.height, onContentResize]);

  const displace = useMemo(() => {
    if (!container || !maxContentHeight) {
      return undefined;
    }

    return container.clientHeight - maxContentHeight;
  }, [container, maxContentHeight]);

  const handleClose = React.useCallback(
    (event: React.MouseEvent) => {
      onClose();
      event.stopPropagation();
    },
    [onClose],
  );

  const theProgress = Number.isNaN(progress) ? 0 : progress;

  return (
    <div
      className={classNames(styles.oneMoreRoot)}
      style={{ '--progress': theProgress, '--level': level } as CSSProperties}
    >
      {backdrop && <Backdrop className={styles.backdrop} isLeaving={isCollapsed} />}
      <section
        className={classNames(styles.root, {
          [styles.moving]: progress > 0,
          [styles.collapsed]: isCollapsed,
        })}
        ref={sheetRef}
        style={displace ? { transform: displace ? `translateY(calc(1px * ${displace}))` : undefined } : sheetStyle}
      >
        <div
          className={classNames(styles.sectionContent, { [styles.sectionMain]: variant === 'main' })}
          ref={(instance) => {
            contentRef.current = instance;
            setContainer(instance);
          }}
        >
          <div className={styles.headerContainer} ref={handleRef}>
            <header className={styles.header} onClick={onClick}>
              {typeof title === 'string' ? (
                <Typography variant="heading4" color="disabled">
                  {title}
                </Typography>
              ) : (
                title
              )}
              <div className={styles.headerActions}>
                {renderActions && renderActions()}
                {noClose ? null : (
                  <ModalCloseButton className={styles.closeButtonFab} variant="fab" onClick={handleClose} />
                )}
              </div>
            </header>
          </div>
          <main className={styles.main}>{children}</main>
        </div>
      </section>
    </div>
  );
};

BottomSheet.Container = ({ children }) => {
  return <div className={styles.container}>{children}</div>;
};

BottomSheet.Content = ({ children, className = '', height = 'content', title }) => {
  return (
    <div
      className={classNames(
        styles.content,
        {
          [styles.contentHeight]: height === 'content',
          [styles.restHeight]: height === '*',
        },
        className,
      )}
    >
      {title && (
        <div className={styles.title}>
          {typeof title === 'string' ? (
            <Typography variant="heading4" color="disabled">
              {title}
            </Typography>
          ) : (
            title
          )}
        </div>
      )}
      {children}
    </div>
  );
};

BottomSheet.Footer = ({ children }) => {
  return (
    <footer className={styles.footer}>
      <BottomSheet.Container>{children}</BottomSheet.Container>
    </footer>
  );
};

export default BottomSheet;
